import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useLocation } from "react-router-dom";
import { DateTime } from "luxon";
import { datadogRum } from "@datadog/browser-rum";
import { Button } from "../../components";
import {
  ButtonLoaderIcon,
  GreenTickIcon,
  SuccessIcon,
} from "../../assets/icons";
import { CredibleLogoLight } from "../../assets/images";
import { LOCALSTORAGE_KEYS } from "../../constants";
import { useMicroFlow } from "../../contexts";
import {
  useApproveCompanyAecbConsent,
  useDeclineCompanyAecbConsent,
  useGetCompanyAecbConsent,
  useTheme,
} from "../../hooks";
import {
  AecbConsentStatus,
  CompanyShareholderAecbConsentContext,
} from "../../hooks/api/types";
import { isEmptyObject } from "../../utils";

const ConsentShieldIcon = () => (
  <svg
    width="48"
    height="48"
    viewBox="0 0 48 48"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <circle cx="24" cy="24" r="24" fill="#0064FF" />
    <path
      d="M24 12.5L33 16.2V23.8C33 29.4 29.1 34.5 24 36.5C18.9 34.5 15 29.4 15 23.8V16.2L24 12.5Z"
      fill="white"
      fillOpacity="0.95"
    />
    <path
      d="M21.2 24.9L23.1 26.8L27.2 22.3"
      stroke="#0064FF"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const DetailItem = ({ label, value }: { label: string; value: ReactNode }) => (
  <div className="cx-flex cx-flex-col cx-gap-1">
    <div className="cx-text-text-secondary cx-text-sm">{label}</div>
    <div className="cx-text-text-primary cx-font-semibold cx-text-base">
      {value}
    </div>
  </div>
);

const StatusCard = ({
  title,
  children,
  icon,
}: {
  title: string;
  children: ReactNode;
  icon?: ReactNode;
}) => (
  <div className="cx-w-full cx-max-w-[640px] cx-bg-white cx-rounded-2xl cx-shadow-sm cx-p-8 md:cx-p-10 cx-flex cx-flex-col cx-items-center cx-gap-4 cx-text-center">
    {icon}
    <div className="cx-text-text-primary cx-font-bold cx-text-2xl">{title}</div>
    <div className="cx-text-text-secondary">{children}</div>
  </div>
);

const renderFacilityAmount = (amount: string | null) => {
  if (!amount) {
    return "—";
  }
  const numeric = amount.replace(/^AED\s*/i, "").trim();
  return (
    <span className="cx-inline-flex cx-items-center cx-gap-1">
      <span className="cx-font-aed-font">D</span>
      {numeric}
    </span>
  );
};

const AecbConsentMicroflow = () => {
  const { search } = useLocation();
  const { setTheme } = useTheme();
  const {
    actions: { update: updateMicroFlowContext },
  } = useMicroFlow();

  const code = useMemo(() => new URLSearchParams(search).get("code"), [search]);

  const [context, setContext] =
    useState<CompanyShareholderAecbConsentContext | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [tokenReady, setTokenReady] = useState(false);

  // Layout effect so the session token is written before react-query's fetch effect.
  useLayoutEffect(() => {
    if (!code) {
      setTokenReady(false);
      return;
    }
    localStorage.setItem(LOCALSTORAGE_KEYS.SESSION_TOKEN, code);
    setTokenReady(true);
  }, [code]);

  const {
    data: fetchedContext,
    isLoading: isLoadingContext,
    isFetching,
    isError: isLoadError,
    isFetched,
  } = useGetCompanyAecbConsent(tokenReady);

  const { mutateAsync: approveConsent, isLoading: isApproving } =
    useApproveCompanyAecbConsent();
  const { mutateAsync: declineConsent, isLoading: isDeclining } =
    useDeclineCompanyAecbConsent();

  useEffect(() => {
    if (!fetchedContext) {
      return;
    }
    setContext(fetchedContext as CompanyShareholderAecbConsentContext);
  }, [fetchedContext]);

  useEffect(() => {
    if (!context) {
      return;
    }

    if (context.dp?.themeConfig && !isEmptyObject(context.dp.themeConfig)) {
      setTheme(context.dp.themeConfig);
    } else {
      setTheme();
    }

    updateMicroFlowContext({
      logo:
        (context.dp?.themeConfig as { logo?: string } | undefined)?.logo ||
        CredibleLogoLight,
      footerLogo: CredibleLogoLight,
    });

    datadogRum.clearUser();
    datadogRum.setUser({
      id: context.consentRecordId,
      entity: "CompanyShareholderSignatory",
      microflow: "AECB_CONSENT",
      signatoryName: context.signatoryName,
      corporateEntityName: context.corporateEntityName,
      distributionPartnerId: context.dp?.id,
      distributionPartnerName: context.dp?.name,
    });
  }, [context]);

  const handleApprove = async () => {
    setActionError(null);
    try {
      const data =
        (await approveConsent()) as CompanyShareholderAecbConsentContext;
      setContext(data);
    } catch (err: unknown) {
      const message =
        (err as { message?: string })?.message ||
        "Unable to approve consent. Please try again.";
      setActionError(message);
    }
  };

  const handleDecline = async () => {
    setActionError(null);
    try {
      const data =
        (await declineConsent()) as CompanyShareholderAecbConsentContext;
      setContext(data);
    } catch (err: unknown) {
      const message =
        (err as { message?: string })?.message ||
        "Unable to decline consent. Please try again.";
      setActionError(message);
    }
  };

  const isLoadingConsent =
    Boolean(code) &&
    (!tokenReady || isLoadingContext || isFetching) &&
    !context;
  const loadError =
    !code ||
    isLoadError ||
    (tokenReady && isFetched && !isFetching && !context);

  if (isLoadingConsent) {
    return (
      <div className="cx-animate-spin cx-text-brand-primary-regular cx-w-10 cx-h-10">
        <ButtonLoaderIcon width="100%" height="100%" />
      </div>
    );
  }

  if (loadError || !context) {
    return (
      <StatusCard title="This consent link is invalid">
        The link may have expired or already been used. Please contact CredibleX
        if you need a new consent request.
      </StatusCard>
    );
  }

  const expiresLabel = context.expiresAt
    ? DateTime.fromISO(context.expiresAt).toFormat("d MMM yyyy")
    : null;

  const isExpired =
    context.isExpired ||
    (context.status === AecbConsentStatus.PENDING &&
      !!context.expiresAt &&
      DateTime.utc() > DateTime.fromISO(context.expiresAt).toUTC());

  const canDecide = context.canDecide && !isExpired;
  const isBusy = isApproving || isDeclining;

  if (context.status === AecbConsentStatus.RECEIVED) {
    return (
      <StatusCard
        title="Consent approved"
        icon={
          <SuccessIcon className="cx-text-brand-primary-regular cx-w-16 cx-h-16" />
        }
      >
        Thank you. We have recorded your consent for{" "}
        <span className="cx-font-semibold cx-text-text-primary">
          {context.corporateEntityName}
        </span>
        . You can close this page.
      </StatusCard>
    );
  }

  if (context.status === AecbConsentStatus.DECLINED) {
    return (
      <StatusCard title="Consent declined">
        You declined to provide AECB consent for{" "}
        <span className="cx-font-semibold cx-text-text-primary">
          {context.corporateEntityName}
        </span>
        . CredibleX has been notified. You can close this page.
      </StatusCard>
    );
  }

  if (isExpired) {
    return (
      <StatusCard title="This request has expired">
        Consent requests are valid for 7 days. Please ask CredibleX to send a
        new request if you still need to respond.
      </StatusCard>
    );
  }

  return (
    <div className="cx-w-full cx-max-w-[640px] cx-bg-white cx-rounded-2xl cx-shadow-sm cx-p-6 md:cx-p-10 cx-flex cx-flex-col cx-items-center">
      <ConsentShieldIcon />

      <h1 className="cx-mt-6 cx-text-text-primary cx-font-bold cx-text-2xl md:cx-text-[28px] cx-leading-tight cx-text-center">
        {context.signatoryName}, as an Authorised Signatory we need your consent
        for a bureau report
      </h1>

      {expiresLabel && (
        <p className="cx-mt-3 cx-text-text-secondary cx-text-sm cx-text-center">
          This request expires {expiresLabel} · 7 days from when it was sent
        </p>
      )}

      <div className="cx-mt-8 cx-w-full cx-rounded-xl cx-border cx-border-stroke-primary cx-bg-[#F8FAFC] cx-p-5">
        <div className="cx-text-text-primary cx-font-semibold cx-text-sm cx-mb-4">
          Request details
        </div>
        <div className="cx-grid cx-grid-cols-1 sm:cx-grid-cols-2 cx-gap-x-8 cx-gap-y-4">
          <DetailItem label="Facility for" value={context.facilityFor} />
          <DetailItem label="Your role" value={context.roleLabel} />
          <DetailItem label="Facility type" value={context.facilityType} />
          <DetailItem
            label="Facility amount"
            value={renderFacilityAmount(context.facilityAmount)}
          />
        </div>
      </div>

      <div className="cx-mt-4 cx-w-full cx-rounded-xl cx-border cx-border-stroke-primary cx-bg-[#F8FAFC] cx-p-5">
        <div className="cx-text-text-primary cx-font-semibold cx-text-sm cx-mb-4">
          What you are consenting to
        </div>
        <div className="cx-flex cx-flex-col cx-gap-3">
          <div className="cx-flex cx-items-start cx-gap-3 cx-rounded-lg cx-bg-white cx-p-3">
            <GreenTickIcon className="cx-shrink-0 cx-mt-0.5" />
            <p className="cx-text-text-primary cx-text-sm cx-leading-relaxed">
              Pull{" "}
              <span className="cx-font-semibold">
                {context.corporateEntityName}&apos;s
              </span>{" "}
              AECB credit report to assess this application
            </p>
          </div>
          <div className="cx-flex cx-items-start cx-gap-3 cx-rounded-lg cx-bg-white cx-p-3">
            <GreenTickIcon className="cx-shrink-0 cx-mt-0.5" />
            <p className="cx-text-text-primary cx-text-sm cx-leading-relaxed">
              Pull it periodically{" "}
              <span className="cx-font-semibold">
                for as long as the facility stays active
              </span>
              , for ongoing monitoring
            </p>
          </div>
        </div>
        <p className="cx-mt-4 cx-text-text-secondary cx-text-xs cx-text-center">
          One decision covers both — no separate monitoring request will follow.
        </p>
      </div>

      {actionError && (
        <p className="cx-mt-4 cx-text-sm cx-text-red-600 cx-text-center">
          {actionError}
        </p>
      )}

      <div className="cx-mt-8 cx-w-full cx-grid cx-grid-cols-1 sm:cx-grid-cols-2 cx-gap-3">
        <Button
          label="Approve"
          fullWidth
          disabled={!canDecide || isBusy}
          loader={isApproving ? "left" : undefined}
          onClick={handleApprove}
        />
        <Button
          label="Decline"
          outlined
          fullWidth
          disabled={!canDecide || isBusy}
          loader={isDeclining ? "left" : undefined}
          onClick={handleDecline}
        />
      </div>
    </div>
  );
};

export default AecbConsentMicroflow;
